import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell, BellOff, CircleUser, EllipsisVertical, Menu, Settings, User } from "@thuthi/ui/icons";
import { Button } from "@thuthi/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@thuthi/ui/components/dropdown-menu";
import session from "../app/Session";
import subscriptionManager from "../app/SubscriptionManager";
import routes from "./routes";
import { shortUrl, topicDisplayName, topicShortUrl } from "../app/utils";
import { SubscriptionPopup } from "./SubscriptionPopup";
import type { Subscription } from "../app/types";

interface ActionBarProps {
  selected?: Subscription | null;
  onMobileDrawerToggle: () => void;
  onUnsubscribe?: (subscription: Subscription) => void;
}

const ActionBar = (props: ActionBarProps) => {
  const { t } = useTranslation();
  const location = useLocation();

  let title = "ntfy";
  if (props.selected) {
    title = topicDisplayName(props.selected);
  } else if (location.pathname === routes.settings) {
    title = t("action_bar_settings");
  } else if (location.pathname === routes.account) {
    title = t("action_bar_account");
  }
  const subtitle = props.selected ? topicShortUrl(props.selected.baseUrl, props.selected.topic) : shortUrl(config.base_url);

  return (
    <header
      // z-40: Below temporary drawers/dialogs (Radix portals are z-50), above scrolling content.
      className="bg-background/90 text-foreground relative z-40 mx-2.5 mt-2.5 box-border flex min-h-16 items-center gap-2 rounded-2xl px-2 py-2 shadow-card backdrop-blur-[22px] sm:mx-6 sm:mt-[18px] sm:min-h-[72px] sm:gap-3 sm:px-3 dark:shadow-[0_18px_55px_rgba(0,0,0,0.42)]"
    >
      <Button
        variant="ghost"
        size="icon"
        aria-label={t("action_bar_show_menu")}
        onClick={props.onMobileDrawerToggle}
        className="bg-muted/60 shrink-0 rounded-full sm:hidden"
      >
        <Menu />
      </Button>
      <div aria-hidden className="hidden size-[42px] shrink-0 place-items-center rounded-xl bg-primary font-extrabold text-primary-foreground sm:grid">
        n
      </div>
      <div className="min-w-0 grow">
        <div className="truncate text-base leading-[1.15] font-extrabold tracking-normal sm:text-[1.16rem]">{title}</div>
        <div className="text-muted-foreground truncate text-xs leading-[1.45] font-semibold">{subtitle}</div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {props.selected && <SettingsIcons subscription={props.selected} onUnsubscribe={props.onUnsubscribe} />}
        <ProfileIcon />
      </div>
    </header>
  );
};

interface SettingsIconsProps {
  subscription: Subscription;
  onUnsubscribe?: (subscription: Subscription) => void;
}

const SettingsIcons = (props: SettingsIconsProps) => {
  const { t } = useTranslation();
  const { subscription } = props;

  const handleToggleMute = async () => {
    const mutedUntil = subscription.mutedUntil ? 0 : 1; // Make this a timestamp in the future
    await subscriptionManager.setMutedUntil(subscription.id, mutedUntil);
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={handleToggleMute}
        aria-label={t("action_bar_toggle_mute")}
        className="bg-muted/60 rounded-full"
      >
        {subscription.mutedUntil ? <BellOff /> : <Bell />}
      </Button>
      <SubscriptionPopup
        subscription={subscription}
        placement="right"
        trigger={
          <Button variant="ghost" size="icon" aria-label={t("action_bar_toggle_action_menu")} className="bg-muted/60 rounded-full">
            <EllipsisVertical />
          </Button>
        }
      />
    </>
  );
};

const ProfileIcon = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <>
      {session.exists() && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label={t("action_bar_profile_title")} className="bg-muted/60 rounded-full">
              <CircleUser />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-44">
            <DropdownMenuItem onSelect={() => navigate(routes.account)}>
              <User />
              <b>{session.username()}</b>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate(routes.settings)}>
              <Settings />
              {t("action_bar_profile_settings")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {!session.exists() && config.enable_login && (
        <Button variant="ghost" onClick={() => navigate(routes.login)} className="m-1" aria-label={t("action_bar_sign_in")}>
          {t("action_bar_sign_in")}
        </Button>
      )}
    </>
  );
};

export default ActionBar;
